import {FontAwesomeIcon as Icon} from "@fortawesome/react-fontawesome"
import {Panel} from "components/Panel"
import {Auction, AuctionStatus, Leading, Leasing, Subjects, Tireset, Vehicle} from "domain-models"
import {hasFlag, make, model, monthYear, number, vehicleAttribute} from 'filters'
import _ from 'lodash'
import moment from 'moment'
import React from "react"
import {setAuctions, showAuction, SubjectType} from 'redux/actions'
import {store} from "store"
import AuctionPrice from "../../components/Auction/AuctionPrice"
import Countdown from "../../components/Countdown"
import ImagePlaceholder from "../../components/ImagePlaceholder"
import AuctionType from "./AuctionDetails/AuctionType"
import WatchButton from "./AuctionDetails/WatchButton"
import {connect} from "react-redux"
import {GlobalState} from "../../state"
import TireData from "./AuctionDetails/TiresetData/TireData"
import Trans from '../../components/Trans'

type AuctionCardOwnProps = {
  auction: Auction
  /** Custom handler for showing details. If not provided, uses default Redux dispatch */
  onShowDetails?: (event: React.MouseEvent, auction: Auction) => void
  /** Custom actions section to render at the bottom instead of bid count and watch button */
  renderActions?: (auction: Auction) => React.ReactNode
  /** Custom badge content to replace the AuctionType badge */
  badgeContent?: React.ReactNode
  /** Custom countdown end date (overrides auction.round.EndsOn) */
  countdownEndDate?: string
  /** Custom price display content */
  renderPrice?: (auction: Auction) => React.ReactNode
}

type AuctionCardReduxProps = {
  auctionFilter: string | null | undefined
  auctions: Array<Auction>
  subjectType: SubjectType
}

type AuctionCardProps = AuctionCardOwnProps & AuctionCardReduxProps

type AuctionCardState = {
  showDetails: boolean
  status: AuctionStatus
  subject?: Subjects
  tireset?: Tireset
  vehicle?: Vehicle
  leading?: Leading
  leasing?: Leasing
}

class AuctionCard extends React.Component<AuctionCardProps, AuctionCardState> {
  constructor(props: AuctionCardProps) {
    super(props)

    let subject

    const vehicle = _.first(props.auction.subjects)?.vehicle
    const tireset = _.first(props.auction.subjects)?.tireset
    const leading = _.first(props.auction.subjects)?.leading
    const leasing = _.first(props.auction.subjects)?.leasing

    subject = vehicle || tireset || leading || leasing

    this.state = {
      showDetails: false,
      status: props.auction.status || 'ongoing',
      subject,
      vehicle,
      tireset,
      leading,
      leasing
    }
  }

  componentDidMount() {
    this.setCountdown()
  }

  componentWillUnmount() {
    this.setCountdown()

    if (this.interval) {
      clearInterval(this.interval)
    }
  }

  componentDidUpdate(prevProps: Readonly<AuctionCardProps>, prevState: Readonly<AuctionCardState>, snapshot?: any) {
    if (this.props.subjectType !== prevProps.subjectType) {

      let subject

      const vehicle = _.first(this.props.auction.subjects)?.vehicle
      const tireset = _.first(this.props.auction.subjects)?.tireset
      const leading = _.first(this.props.auction.subjects)?.leading
      const leasing = _.first(this.props.auction.subjects)?.leasing

      subject = vehicle || tireset || leading || leasing

      this.setState({
        subject,
        vehicle,
        tireset,
        leading,
        leasing,
      })
    }
  }

  showDetails = (event: React.MouseEvent, auction: Auction) => {
    event.preventDefault()
    event.stopPropagation()
    
    // Use custom handler if provided
    if (this.props.onShowDetails) {
      this.props.onShowDetails(event, auction)
      return
    }
    
    // Default behavior - dispatch Redux action
    if (this.props.auction.status !== 'ended' || this.props.auctionFilter === 'auctioned') {
      store.dispatch(showAuction(auction))
    }
  }

  render() {
    if (!this.state.subject) {
      return <></>
    }
    const auction = this.props.auction
    let firstImage = _.first(this.state?.subject?.images || [])

    const disabled = (this.state.status === 'ended' || this.state.status === 'not-started-yet') && _.get(store.getState(), 'auctions.filter.type', false) !== 'auctioned'
    const dealer = this.state.subject.dealer
    const dealerLocation = dealer ? [dealer.FirmExtra, dealer.Street, dealer.PostalCode, dealer.City].filter(Boolean) : []
    return (
      <div className="flex flex-col w-full md:w-1/2 xl:w-1/3 max-w-2xl lg:max-w-3xl">
        <Panel full_height={true} disabled={disabled}>
          <div className={"flex flex-col text-gray-700 bg-gray-100" + (!disabled ? ' cursor-pointer' : '')}>
            {firstImage ?
              <div className="bg-cover bg-center h-64" style={{backgroundImage: "url(" + firstImage + ")"}} onClick={(event) => this.showDetails(event, auction)}/>
              :
              <ImagePlaceholder/>
            }
            <div className='flex flex-col justify-between h-full'>
              <div onClick={(event) => this.showDetails(event, auction)} className="mb-2">
                {/* Badge - custom or default AuctionType */}
                <div className="flex flex-row items-center gap-2">
                  {this.props.badgeContent !== undefined ? this.props.badgeContent : <AuctionType auctionType={auction.round?.Type}/>}
                  
                  {/* Outbid indicator for Type 0 */}
                  {!!auction.isOutbid && auction.round?.Type === 0 && auction.round?.bid && (
                    <div className="inline-flex items-center px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded">
                      <Icon icon='exclamation-triangle' className="mr-1" />
                      <Trans>Überboten</Trans>
                    </div>
                  )}
                </div>
                
                {/* Countdown - custom date or default */}
                <div>
                  <Countdown 
                    auction={auction} 
                    timeTillDate={this.props.countdownEndDate || auction.round?.EndsOn} 
                    hotBid={this.props.auction.status === 'hot-bid'}
                  />
                </div>

                <div className={"px-1 text-sm"}>
                  {dealerLocation.join(', ')}
                </div>

                {/* Price - custom or default */}
                <div className="-mx-2">
                  {this.props.renderPrice ? this.props.renderPrice(auction) : <AuctionPrice auction={auction}/>}
                </div>

                {this.props.subjectType === 'Vehicle' && this.state.vehicle ?
                  <div className="font-semibold leading-relaxed text-base px-2 truncate mb-2">
                    {make(this.state.subject.Make)} {this.state.vehicle.ShortDescription}
                  </div>
                  : this.props.subjectType === 'Tireset' ?
                    <div className="font-semibold leading-relaxed text-base px-2 truncate mb-2">
                      <Trans>Radsatz für</Trans> {make(this.state.subject.Make)} {model(this.state.subject.Make, this.state.subject.Model)}
                    </div>
                    : null
                }

                {
                  this.props.subjectType === 'Vehicle' && this.state.vehicle ?
                    <div className='px-2 mt-2 flex flex-row flex-wrap text-gray-800 font-semibold'>
                      <span className='w-1/2 md:w-1/3 whitespace-no-wrap overflow-hidden'>
                        <Icon className="text-gray-600 font-normal fa-fw" icon="tachometer-alt"/> {number(this.state.vehicle.Kilometers, 0, 'km')}
                      </span>
                      <span className='w-1/2 md:w-1/3 whitespace-no-wrap overflow-hidden'>
                        <Icon className="text-gray-600 font-normal fa-fw" icon="calendar-alt"/> {monthYear(this.state.vehicle.RegistrationDate)}
                      </span>
                      <span className='w-1/2 md:w-1/3 whitespace-no-wrap overflow-hidden'>
                        <Icon className="text-gray-600 font-normal fa-fw" icon="burn"/> {number(this.state.vehicle.Power, 0, 'kw')}
                      </span>
                      <span className='w-1/2 md:w-1/3 whitespace-no-wrap overflow-hidden'>
                        <Icon className="text-gray-600 font-normal fa-fw" icon="bolt"/> <Trans>{vehicleAttribute('Transmission', this.state.vehicle.Transmission)}</Trans>
                      </span>
                      <span className='w-1/2 md:w-1/3 whitespace-no-wrap overflow-hidden'>
                        <Icon className="text-gray-600 font-normal fa-fw" icon="gas-pump"/> <Trans>{vehicleAttribute('Fuel', this.state.vehicle.Fuel)}</Trans>
                      </span>
                      <span className='w-full whitespace-no-wrap overflow-hidden'>
                        {
                          hasFlag(this.state.vehicle, 'SellFlags', 32768) ?
                            <span><Icon className="text-gray-600 font-normal fa-fw" icon="search-dollar"/> <Trans>MwST. ausweisbar</Trans></span>
                            :
                            <span><Icon className="text-gray-600 font-normal fa-fw" icon="search-dollar"/> <Trans>MwST. nicht ausweisbar</Trans></span>
                        }
                      </span>
                      {this.state.vehicle.envkv?.EmissionsCombined
                        ? <span className='w-1/2 md:w-1/3 whitespace-no-wrap overflow-hidden'>
                            <Icon className="text-gray-600 font-normal fa-fw" icon="leaf"/> {number(this.state.vehicle.envkv.EmissionsCombined, 0, 'g/km')}
                          </span>
                        : null
                      }
                      {this.state.vehicle.envkv?.EmissionsCombinedWeighted
                        ? <span className='w-1/2 md:w-1/3 whitespace-no-wrap overflow-hidden'>
                            <Icon className="text-gray-600 font-normal fa-fw" icon="leaf"/> {number(this.state.vehicle.envkv.EmissionsCombinedWeighted, 0, 'g/km')} <Trans>gew.</Trans>
                          </span>
                        : null
                      }
                      {this.state.vehicle.Weight
                        ? <span className='w-1/2 md:w-1/3 whitespace-no-wrap overflow-hidden'>
                            <Icon className="text-gray-600 font-normal fa-fw" icon="weight"/> {number(this.state.vehicle.Weight, 0, 'kg')}
                          </span>
                        : null
                      }
                    </div>
                    :
                    <div className='px-2 mt-2 flex flex-row flex-wrap text-gray-800 font-semibold space-x-3'>
                      <span className='w-1/2 md:w-1/3 whitespace-nowrap overflow-hidden flex truncate'>
                        <svg className={'text-gray-600 font-normal fa-fw'} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"/>
                        </svg>
                        <span className={'ml-2'}>{this.state.tireset ? this.state.tireset.Notes : null}</span>
                      </span>

                      {
                        this.state.tireset ?
                          <TireData tireset={this.state.tireset} onlyFirst={true}/>
                          : null
                      }
                    </div>
                }
              </div>

              {/* Actions section - custom or default */}
              {this.props.renderActions ? (
                this.props.renderActions(auction)
              ) : (
                <>
                  {
                    _.get(store.getState(), 'auctions.filter.type', false) === 'auctioned' ? null :
                      <div className="border-t border-gray-300"/>
                  }

                  {
                    _.get(store.getState(), 'auctions.filter.type', false) === 'auctioned' ? null :
                      <div className={"cursor-default bg-gray-100 pb-2 px-4 flex flex-row flex-wrap justify-between text-sm mt-1 items-center mt-2"}>
                        <div className="font-semibold text-500 flex justify-start w-1/3">
                          {
                            auction.round?.Type === 1 ?
                              <span>
                                <Icon icon='gavel'/> {auction.round?.bids?.length || 0} {auction.round?.bids?.length === 1 ? <Trans>Gebot</Trans> : <Trans>Gebote</Trans>}
                              </span>
                              : null
                          }
                        </div>
                        <div className={"font-semibold flex justify-end w-1/3"}>
                          <WatchButton auction={auction}/>
                        </div>
                      </div>
                  }
                </>
              )}
            </div>
          </div>
        </Panel>
      </div>
    )
  }

  private setCountdown = () => {
    const timeTillDate = this.props.countdownEndDate || this.props.auction.round?.EndsOn
    if (!timeTillDate) return
    
    const now = moment()
    const countdown = moment(timeTillDate)

    const days = countdown.diff(now, 'days')
    const hours = countdown.subtract(days, 'days').diff(now, 'hours')
    const minutes = countdown.subtract(hours, 'hours').diff(now, 'minutes')
    const seconds = countdown.subtract(minutes, 'minutes').diff(now, 'seconds')

    let status = 'ongoing' as AuctionStatus
    if (!days && !hours && minutes < 5) {
      status = 'hot-bid'
    }

    if (days <= 0 && hours <= 0 && minutes <= 0 && seconds <= 0) {
      status = 'ended'
    }

    if (this.props.auction.round?.StartsOn && moment(this.props.auction.round.StartsOn).isAfter(now)) {
      status = 'not-started-yet'
    }

    if (this.state.status !== status) {
      this.setState({status})
      this.props.auction.status = status
      if (this.props.auctions) {
        store.dispatch(setAuctions(this.props.auctions))
      }
    }
  }

  private interval = setInterval(this.setCountdown, 1000)
}

export default connect((state: GlobalState) => ({
  auctionFilter: state.auctions.filter.type,
  auctions: state.auctions.auctions,
  subjectType: state.app.subjectType
}))(AuctionCard)
